/**
 * GATILHOS DE GRUPO (spec 21) — entrou, saiu, lotou.
 *
 * ## O que esta tela nunca faz
 *
 * 1. **Não deixa ligar entrada ou saída sem dizer o custo.** As duas ABREM CONVERSA, e conversa
 *    aberta consome a franquia do mês (ADR-0012). Com uma porta de entrada enchendo grupos, cem
 *    pessoas entrando são cem conversas — não cem mensagens. O aviso fica numa linha fixa acima da
 *    lista, sem ninguém pedir; o resto da explicação mora no (i).
 * 2. **Não confunde desativar com apagar.** Apagar leva junto a memória de quem já recebeu, e
 *    recriar o gatilho cumprimenta de novo todo mundo que ainda estiver no grupo. Os dois botões têm
 *    nomes diferentes e o de apagar pede confirmação dizendo o que perde.
 * 3. **Não oferece `{{nome}}`.** O nome de quem entrou não vem no evento do WhatsApp — só o JID.
 *    Uma variável que sai vazia metade das vezes é pior que não existir.
 *
 * ## A família (spec 22)
 *
 * Consulta dentro do módulo de Grupos (`ModuloDeGrupos`, aba Automático › Gatilhos) + `ListView`, uma linha por
 * gatilho. A confirmação de apagar é `Dialogo`, com a frase inteira e as duas saídas.
 *
 * ## Novo gatilho (01/11)
 *
 * Até 01/11 a tela dizia "Escolha um grupo, o evento e a mensagem" e não havia onde escolher: o `POST /gatilhos`
 * existia e nenhuma tela o chamava. "Novo gatilho" é a primária da aba, num `Dialogo` curto (grupo, evento, número,
 * mensagem; o limite só na lotação). O custo vem ANTES do botão: a prévia do servidor diz quantas conversas aquele
 * grupo teria aberto nos últimos 30 dias.
 */

import { useId, useState, type ReactNode } from "react";
import { EstadoDaFalha, chamar, falhaDe, identificadorLegivel, textos, useSessao } from "@hugi/ui";
import {
  Dialogo, Dica, Erro, ListView, Selo, SemPermissao,
  type ColunaDaLista, type VistaDaLista,
} from "@hugi/ui/kit";
import { Zap } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { chavesDeGatilhos, useGatilhos, useGrupos, type ConexaoParaPublicar, type GatilhoNaLista } from "./dados.js";
import "./hugi-grupos.css";
import { ModoDoAutomatico, ModuloDeGrupos } from "./modulo-de-grupos.js";

const NOME_DO_EVENTO: Record<GatilhoNaLista["evento"], string> = {
  entrada: "Quando alguém entra",
  saida: "Quando alguém sai",
  lotou: "Quando o grupo enche",
};

const DESTINO_DO_EVENTO: Record<GatilhoNaLista["evento"], string> = {
  entrada: "mensagem privada para quem entrou",
  saida: "mensagem privada para quem saiu",
  lotou: "mensagem no próprio grupo",
};

const TITULO = "Gatilhos de grupo";
const FRASE = "Mensagem automática quando alguém entra, quando sai, e quando o grupo enche.";
const CUSTO = "Entrada e saída abrem conversa com a pessoa, e cada conversa aberta conta na franquia do mês.";

const VISTAS: VistaDaLista<GatilhoNaLista>[] = [
  { id: "todos", rotulo: "Todos os gatilhos", passa: () => true },
  { id: "ligados", rotulo: "Só os ligados", passa: (g) => g.ativo },
  { id: "desligados", rotulo: "Só os desligados", passa: (g) => !g.ativo },
];

export function GatilhosDeGrupo() {
  const consulta = useGatilhos();
  const clienteDeConsulta = useQueryClient();
  const sessao = useSessao();
  const podeCriar = sessao.permissoes.includes("manage_post");
  const [apagando, setApagando] = useState<GatilhoNaLista | null>(null);
  const [criando, setCriando] = useState(false);
  const primaria = (
    <Acao
      tom="primaria"
      desabilitado={!podeCriar}
      {...(podeCriar ? {} : { motivo: "Criar gatilho exige a permissão manage_post." })}
      onClick={() => setCriando(true)}
    >
      Novo gatilho
    </Acao>
  );

  const alternar = useMutation({
    mutationFn: (g: GatilhoNaLista) =>
      chamar<{ id: string }>("/api/painel/gatilhos", {
        metodo: "POST",
        corpo: {
          grupoId: g.grupoId, conexaoId: g.conexaoId, evento: g.evento, texto: g.texto,
          ritmoPorMinuto: g.ritmoPorMinuto, ativo: !g.ativo,
          ...(g.evento === "lotou" ? { limite: g.limite } : {}),
        },
      }),
    onSuccess: () => void clienteDeConsulta.invalidateQueries({ queryKey: chavesDeGatilhos.lista() }),
  });

  const apagar = useMutation({
    mutationFn: (id: string) => chamar<Record<string, never>>(`/api/painel/gatilhos/${id}`, { metodo: "DELETE" }),
    onSuccess: () => void clienteDeConsulta.invalidateQueries({ queryKey: chavesDeGatilhos.lista() }),
  });

  // O aviso do custo: uma linha, fixa, acima da lista — está na tela antes de qualquer botão de ligar.
  const aviso = (
    <p role="note" className="hugi-grupos-faixa">
      Cem pessoas entrando num grupo com gatilho ligado são cem conversas — não cem mensagens.
      <Dica texto={CUSTO} />
    </p>
  );

  if (consulta.isError) {
    return (
      <ModuloDeGrupos ativa="automatico" primaria={primaria}>
        {aviso}
        <div className="v2-cartao"><Falha erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /></div>
      </ModuloDeGrupos>
    );
  }

  const ocupado = alternar.isPending || apagar.isPending;
  const falhaDaAcao = alternar.isError ? alternar.error : apagar.isError ? apagar.error : null;

  const colunas: ColunaDaLista<GatilhoNaLista>[] = [
    { id: "grupo", rotulo: "Grupo", celula: (g) => <strong>{g.grupoNome}</strong>, dica: (g) => g.grupoNome, ordenar: (g) => g.grupoNome },
    { id: "estado", rotulo: "Estado", celula: (g) => <Selo tom={g.ativo ? "sucesso" : "neutro"}>{g.ativo ? "Ligado" : "Desligado"}</Selo>, ordenar: (g) => (g.ativo ? 1 : 0) },
    {
      id: "evento", rotulo: "Evento", principal: true,
      celula: (g) => (
        <span className="hugi-grupos-duas-linhas">
          <span>{NOME_DO_EVENTO[g.evento]}</span>
          <small>{DESTINO_DO_EVENTO[g.evento]}{g.evento === "lotou" && g.limite !== null ? ` (a partir de ${g.limite})` : ""}</small>
        </span>
      ),
      ordenar: (g) => g.evento,
    },
    {
      id: "numero", rotulo: "Por",
      celula: (g) => <>{g.conexaoRotulo ?? <span className="is-ausente">número sem apelido</span>}{g.evento !== "lotou" ? ` · ${g.ritmoPorMinuto}/min` : ""}</>,
      ordenar: (g) => g.conexaoRotulo,
    },
    { id: "participantes", rotulo: "No grupo", numerica: true, celula: (g) => g.participantes, ordenar: (g) => g.participantes },
    {
      id: "envios", rotulo: "Envios",
      celula: (g) => (
        <span>
          {g.enviados} enviada{g.enviados === 1 ? "" : "s"}
          {g.pulados > 0 ? ` · ${g.pulados} pulada${g.pulados === 1 ? "" : "s"}` : ""}
          {g.falhados > 0 ? ` · ${g.falhados} falhou` : ""}
        </span>
      ),
      ordenar: (g) => g.enviados,
    },
    {
      id: "acoes", rotulo: "Ações", acoes: true,
      celula: (g) => (
        <span className="hugi-acoes-da-linha">
          <Acao pequeno desabilitado={ocupado} onClick={() => alternar.mutate(g)}>{g.ativo ? "Desligar" : "Ligar"}</Acao>
          <Acao pequeno onClick={() => setApagando(g)}>Apagar</Acao>
        </span>
      ),
    },
    { id: "mensagem", rotulo: "Mensagem", celula: (g) => <span className="hugi-grupos-texto">{g.texto}</span>, dica: (g) => g.texto },
  ];

  return (
    <ModuloDeGrupos ativa="automatico" primaria={primaria}>
      {aviso}
      <ListView<GatilhoNaLista>
        titulo={TITULO}
        icone={<Zap size={18} />}
        colunas={colunas}
        linhas={consulta.data?.itens as GatilhoNaLista[] | undefined}
        chave={(g) => g.id}
        vistas={VISTAS}
        memoria="hugi:gatilhos"
        busca={{ campos: (g) => [g.grupoNome, g.texto, g.conexaoRotulo], placeholder: "Grupo ou mensagem" }}
        contagem={(n) => (n === 1 ? "1 gatilho" : `${n} gatilhos`)}
        vazio={{
          semNada: <><strong>Nenhum gatilho ainda</strong>{" "}<span>Em Novo gatilho, escolha o grupo, o evento e a mensagem. Quem entrar recebe no privado; quando o grupo encher, o aviso sai no próprio grupo.</span></>,
          semResultado: "Nenhum gatilho nesta vista.",
        }}
        acoes={<><ModoDoAutomatico ativo="gatilhos" /><Dica texto={FRASE} /></>}
        {...(falhaDaAcao ? { avisos: <span className="v2-erro" role="alert"> · {mensagemDe(falhaDaAcao)}</span> } : {})}
      />

      <Dialogo
        aberto={apagando !== null}
        aoFechar={() => setApagando(null)}
        titulo={apagando ? `Apagar o gatilho de ${apagando.grupoNome}?` : "Apagar o gatilho?"}
        rodape={
          <>
            <Acao onClick={() => setApagando(null)}>Cancelar</Acao>
            <Acao tom="perigo" desabilitado={ocupado} onClick={() => { const g = apagando; setApagando(null); if (g) apagar.mutate(g.id); }}>
              Apagar mesmo assim
            </Acao>
          </>
        }
      >
        <p className="hugi-grupos-aviso">
          Apagar perde a memória de quem já recebeu — recriar cumprimenta todo mundo de novo. Para só parar, use Desligar.
        </p>
      </Dialogo>

      {criando ? (
        <NovoGatilho
          conexoes={(consulta.data?.conexoes ?? []) as readonly ConexaoParaPublicar[]}
          aoFechar={() => setCriando(false)}
          aoCriar={() => { setCriando(false); void clienteDeConsulta.invalidateQueries({ queryKey: chavesDeGatilhos.lista() }); }}
        />
      ) : null}
    </ModuloDeGrupos>
  );
}

// --- Novo gatilho -----------------------------------------------------------------------------------

type Evento = GatilhoNaLista["evento"];

/** O formulário curto do gatilho novo. O servidor valida tudo; aqui só não deixa mandar o que falta. */
export function NovoGatilho(props: { readonly conexoes: readonly ConexaoParaPublicar[]; readonly aoFechar: () => void; readonly aoCriar: () => void }) {
  const grupos = useGrupos({});
  const [grupoId, setGrupoId] = useState("");
  const [evento, setEvento] = useState<Evento>("entrada");
  const [conexaoId, setConexaoId] = useState(props.conexoes[0]?.id ?? "");
  const [texto, setTexto] = useState("");
  const [limite, setLimite] = useState("1000");
  const previa = useQuery({
    queryKey: ["gatilhos", "previa", grupoId, evento],
    enabled: Boolean(grupoId) && evento !== "lotou",
    queryFn: () => chamar<{ eventosEm30Dias: number; abreConversa: boolean }>(`/api/painel/gatilhos/previa?grupo=${encodeURIComponent(grupoId)}&evento=${evento}`),
  });
  const criar = useMutation({
    mutationFn: () =>
      chamar<{ id: string }>("/api/painel/gatilhos", {
        metodo: "POST",
        corpo: { grupoId, conexaoId, evento, texto: texto.trim(), ativo: true, ...(evento === "lotou" ? { limite: Number(limite) } : {}) },
      }),
    onSuccess: props.aoCriar,
  });
  const falta = !grupoId ? "Escolha o grupo." : !conexaoId ? "Escolha o número que manda a mensagem." : !texto.trim() ? "Escreva a mensagem." : null;
  return (
    <Dialogo
      aberto
      aoFechar={props.aoFechar}
      titulo="Novo gatilho"
      rodape={
        <>
          <Acao onClick={props.aoFechar}>Cancelar</Acao>
          <Acao tom="primaria" desabilitado={falta !== null || criar.isPending} {...(falta ? { motivo: falta } : {})} onClick={() => criar.mutate()}>
            {criar.isPending ? "Criando…" : "Criar gatilho"}
          </Acao>
        </>
      }
    >
      <div className="hugi-grupos-formulario">
        <label className="hugi-grupos-campo">
          <span>Grupo</span>
          <select className="campo" value={grupoId} onChange={(e) => setGrupoId(e.target.value)} aria-label="Grupo">
            <option value="">{grupos.isPending ? "Carregando…" : "Escolha o grupo…"}</option>
            {(grupos.data?.itens ?? []).map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
          </select>
        </label>
        <label className="hugi-grupos-campo">
          <span>Quando</span>
          <select className="campo" value={evento} onChange={(e) => setEvento(e.target.value as Evento)} aria-label="Evento">
            {(Object.keys(NOME_DO_EVENTO) as Evento[]).map((ev) => <option key={ev} value={ev}>{NOME_DO_EVENTO[ev]} — {DESTINO_DO_EVENTO[ev]}</option>)}
          </select>
        </label>
        {evento === "lotou" ? (
          <label className="hugi-grupos-campo">
            <span>Cheio a partir de (participantes)</span>
            <input className="campo" type="number" min={2} max={1024} value={limite} onChange={(e) => setLimite(e.target.value)} aria-label="Limite de participantes" />
          </label>
        ) : null}
        <label className="hugi-grupos-campo">
          <span>Por qual número</span>
          <select className="campo" value={conexaoId} onChange={(e) => setConexaoId(e.target.value)} aria-label="Por qual número">
            {props.conexoes.length === 0 ? <option value="">Nenhuma conexão da API Hugi</option> : null}
            {props.conexoes.map((c) => <option key={c.id} value={c.id}>{c.apelido ?? (c.numeroExibicao ? identificadorLegivel(c.numeroExibicao) : c.id)}</option>)}
          </select>
        </label>
        <label className="hugi-grupos-campo">
          <span>Mensagem</span>
          <textarea className="campo" rows={3} value={texto} onChange={(e) => setTexto(e.target.value)} aria-label="Mensagem" />
        </label>
        {evento !== "lotou" && grupoId && previa.data ? (
          <p role="note" className="hugi-grupos-aviso">
            Nos últimos 30 dias, este gatilho teria aberto <strong>{previa.data.eventosEm30Dias}</strong> conversa{previa.data.eventosEm30Dias === 1 ? "" : "s"} da franquia.
          </p>
        ) : null}
        {criar.isError ? <p className="v2-erro" role="alert">{mensagemDe(criar.error)}</p> : null}
      </div>
    </Dialogo>
  );
}

// --- Miúdos da tela ---------------------------------------------------------------------------------

/** A frase do servidor quando ele deu uma; sem ela, a genérica. */
function mensagemDe(erro: unknown): string {
  return falhaDe(erro)?.erro.mensagem ?? textos.estados.erro.corpo;
}

/** O estado de falha da área, com os estados do kit (e o da casca quando é caso comercial ou de versão). */
function Falha(props: { readonly erro: unknown; readonly aoTentarDeNovo?: () => void }) {
  const f = falhaDe(props.erro);
  if (f?.tipo === "sem_permissao") return <SemPermissao>{f.permissao ? textos.estados.semPermissao.qual(f.permissao) : textos.estados.semPermissao.semNome}</SemPermissao>;
  if (f?.tipo === "sem_entitlement" || f?.tipo === "versao_incompativel") return <EstadoDaFalha falha={f} />;
  return <Erro {...(props.aoTentarDeNovo ? { aoTentarDeNovo: props.aoTentarDeNovo } : {})}>{f?.erro.mensagem ?? textos.estados.erro.corpo}</Erro>;
}

/** Botão do kit; desabilitado, diz o motivo (no `title` e ligado por `aria-describedby`). */
function Acao(props: {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly tom?: "primaria" | "perigo";
  readonly pequeno?: boolean;
  readonly desabilitado?: boolean;
  readonly motivo?: string;
}) {
  const id = useId();
  const comMotivo = Boolean(props.desabilitado && props.motivo);
  return (
    <>
      <button
        type="button"
        className={`v2-botao${props.tom === "primaria" ? " is-primario" : props.tom === "perigo" ? " is-perigo" : ""}${props.pequeno ? " is-pequeno" : ""}`}
        disabled={props.desabilitado}
        title={comMotivo ? props.motivo : undefined}
        aria-describedby={comMotivo ? id : undefined}
        onClick={props.onClick}
      >
        {props.children}
      </button>
      {comMotivo ? <span id={id} className="v2-sr">{props.motivo}</span> : null}
    </>
  );
}
